import React, { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import useAxios from 'axios-hooks'
import * as Icon from 'react-feather'
import { NumberParam, StringParam, withDefault } from 'use-query-params'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  NavItem,
} from 'reactstrap'
import Select from 'react-select'

import { useSearch } from '~/hooks/useSearch'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import {
  ActiveNPSWrapper,
  ButtonTemplate,
  Container,
  CustomPagination,
  FiltersAndSearchWrapper,
  Header,
  ListingBody,
  MobileSearchContainer,
  NoContentWrapper,
  StyledNav,
  TabsAndSortWrapper,
} from './styles'
import InfoIcon from 'assets/img/svg/nps-info.svg'
import BreadcrumbHeader from '~/components/BreadcrumbHeader'
import NPSCard from '~/components/NPSCards'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import NPSReviewCard from '~/components/NPSReviewCard'
import EmptyBoxSvg from '~/components/EmptyBoxSvg'
import LoadableComponent from '~/components/commons/LoadableComponent'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import { Show } from '~/components/commons'
import { CLIENT_ADMIN } from '~/constants/roles'
import NPSFilterHandler from '~/components/Filters/NPS/Handler'
import FilterButton from '~/components/FilterButton'

function NPS() {
  const intl = useIntl()
  const profile = useSelector(state => state.user?.profile)
  const accountId = profile?.account?.id
  const userRole = profile?.role
  const { search, setSearch } = useSearch()
  const { query, setQuery } = useSearchParamsHandler()
  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
    review: query.review,
    date: query.date,
  })
  const [filterOn, setFilterOn] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [tabId, setTabId] = useState(0)

  const [{ data: NPSStatus, loading: loadingNPSStatus }] = useAxios({
    url: `/nps/account/${accountId}`,
    method: 'GET',
  })

  const [{ data, loading }] = useAxios({
    url: '/nps',
    method: 'GET',
    params: {
      ...query,
    },
  })
  const hasFilter = Boolean(
    query.states.length ||
      query.cities.length ||
      query.areas.length ||
      query.labels.length ||
      query.locations.length ||
      query.review.length ||
      query.date.length
  )

  function handleNavClick(id) {
    if (id === tabId) return
    const tabsNav = document.querySelector('.nav.nav-tabs')
    const tabs = tabsNav.childNodes
    tabs.forEach((tab, index) => {
      if (index === id) return tab.classList.add('active')
      tab.classList.remove('active')
    })

    setTabId(id)
  }

  const total = data?.total || 0
  const totalPages = React.useMemo(() => Math.ceil(total / query.limit), [
    total,
    query.limit,
  ])
  const isMobile = window.innerWidth < 768

  const isActive = !NPSStatus ? true : NPSStatus.active
  const pages = useMemo(() => {
    const MAX_PAGES_PAGINATION = isMobile ? 5 : 10
    const HALF_PAGES_PAGINATION = Math.ceil(MAX_PAGES_PAGINATION / 2)
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      const frontPageNum =
        query.page < HALF_PAGES_PAGINATION
          ? HALF_PAGES_PAGINATION + (HALF_PAGES_PAGINATION - query.page)
          : query.page > totalPages - (HALF_PAGES_PAGINATION - 1)
          ? totalPages - query.page + 1
          : HALF_PAGES_PAGINATION
      const backPageNum =
        query.page < HALF_PAGES_PAGINATION
          ? query.page
          : query.page > totalPages - (HALF_PAGES_PAGINATION - 1)
          ? MAX_PAGES_PAGINATION - 1 - (totalPages - query.page)
          : HALF_PAGES_PAGINATION

      if (i <= query.page - backPageNum) continue
      if (i >= query.page + frontPageNum) break
      pages.push(i)
    }
    return pages
  }, [totalPages, query.page, isMobile])

  const orderOptions = [
    {
      value: 'newest',
      label: <FormattedMessage id="Most recent" />,
    },
    {
      value: 'oldest',
      label: <FormattedMessage id="Older" />,
    },
    {
      value: 'best',
      label: <FormattedMessage id="Top rated" />,
    },
    {
      value: 'worst',
      label: <FormattedMessage id="Worst rated" />,
    },
  ]

  function handleOrderBy(value) {
    setQuery('orderBy', value)
  }

  function handlePaginate(page) {
    setQuery('page', page)
  }

  function handlePageNext() {
    if (query.page === totalPages) return
    const nextPage = query.page + 1
    setQuery('page', nextPage)
  }

  function handlePagePrev() {
    if (query.page === 1) return
    const prevPage = query.page - 1
    setQuery('page', prevPage)
  }

  function handleDropdownToggle() {
    setDropdownOpen(prevState => !prevState)
  }

  function handleDropdownClick(event) {
    const value = event.target.innerText
    setQuery('limit', Number(value))
    setDropdownOpen(false)
  }

  const isLoading = loading || loadingNPSStatus
  return (
    <LoadableComponent loading={isLoading}>
      <Container>
        <NPSFilterHandler onClose={() => setFilterOn(false)} open={filterOn} />

        <Header>
          <BreadcrumbHeader
            path={[{ name: intl.formatMessage({ id: 'NPS' }), path: '/' }]}
          />
          <Show condition={userRole >= CLIENT_ADMIN}>
            <ButtonTemplate to="/account/settings?t=4">
              <Icon.Settings height={14} />
              <FormattedMessage id="Settings" />
            </ButtonTemplate>
          </Show>
        </Header>
        {!isMobile && isActive && (
          <NPSCard
            detractors={data?.counts?.detractors}
            neutrals={data?.counts?.neutrals}
            promoters={data?.counts?.promoters}
            total={data?.total}
          />
        )}
        <FiltersAndSearchWrapper>
          <h1>
            <FormattedMessage id="NPS" />
          </h1>

          <div>
            <FilterButton
              isActive={filterOn}
              filterCount={filterCount}
              onClick={() => setFilterOn(prevState => !prevState)}
            />
          </div>
        </FiltersAndSearchWrapper>
        <MobileSearchContainer>
          <div className="input-search">
            <Icon.Search color="#8D8D8D" size={20} className="search-icon" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              type="text"
              placeholder={intl.formatMessage({ id: 'To look for' })}
            />
          </div>
        </MobileSearchContainer>
        {isMobile && isActive && (
          <NPSCard
            detractors={data?.counts?.detractors}
            neutrals={data?.counts?.neutrals}
            promoters={data?.counts?.promoters}
            total={data?.total}
          />
        )}
        <TabsAndSortWrapper>
          <div className="reviews-tab" data-disabled={!isActive}>
            <StyledNav tabs>
              <NavItem
                className="active"
                onClick={() => {
                  handleNavClick(0)
                  setQuery('status', 'all')
                }}
              >
                <FormattedMessage id="All" />
              </NavItem>
              <NavItem
                onClick={() => {
                  handleNavClick(1)
                  setQuery('status', 'wantContact')
                }}
              >
                <FormattedMessage id="Want contact" />
              </NavItem>
            </StyledNav>
          </div>
          {isActive && (
            <div
              className={
                isMobile
                  ? 'w-100 d-flex align-items-center justify-content-between'
                  : 'd-flex align-items-center'
              }
            >
              <div className="count-text mr-2">
                {(isActive && total) || 0} <FormattedMessage id="reviews" />
              </div>
              <Select
                isDisabled={!isActive}
                className="filter-select"
                placeholder={intl.formatMessage({ id: 'Sort by' })}
                onChange={(selected, helper) => {
                  if (helper.action === 'clear') {
                    handleOrderBy(null)
                  } else {
                    handleOrderBy(selected.value)
                  }
                }}
                value={orderOptions.find(
                  option => option.value === query.orderBy
                )}
                options={orderOptions}
                isClearable
              />
            </div>
          )}
        </TabsAndSortWrapper>
        {isActive && (
          <ListingBody>
            {Boolean(!data?.reviews?.length && !isLoading) && (
              <NoContentWrapper>
                <EmptyBoxSvg />
                <span>
                  <FormattedMessage
                    id={
                      hasFilter
                        ? `No results found with filter`
                        : `Your location doesn't have any reviews yet`
                    }
                  />
                </span>
              </NoContentWrapper>
            )}
            {Boolean((data?.reviews || [])?.length && !isLoading) && (
              <>
                {data.reviews.map(review => (
                  <NPSReviewCard
                    key={review.id}
                    review={review}
                    address={data.address}
                  />
                ))}
                <div className="review-pagination">
                  <div className="d-flex flex-row justify-content-center align-items-center">
                    <Dropdown
                      isOpen={dropdownOpen}
                      toggle={handleDropdownToggle}
                      defaultValue={query.limit}
                    >
                      <DropdownToggle caret>
                        {query.limit}{' '}
                        {dropdownOpen ? (
                          <Icon.ChevronUp color="#A8A8A8" size="15" />
                        ) : (
                          <Icon.ChevronDown color="#A8A8A8" size="15" />
                        )}
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={handleDropdownClick}>
                          10
                        </DropdownItem>
                        <DropdownItem onClick={handleDropdownClick}>
                          20
                        </DropdownItem>
                        <DropdownItem onClick={handleDropdownClick}>
                          30
                        </DropdownItem>
                        <DropdownItem onClick={handleDropdownClick}>
                          40
                        </DropdownItem>
                        <DropdownItem onClick={handleDropdownClick}>
                          50
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                    <div className="ml-2">
                      <FormattedMessage id={'per page'} />
                    </div>
                  </div>
                  <CustomPagination>
                    <button
                      disabled={query.page === 1}
                      onClick={handlePagePrev}
                    >
                      <Icon.ChevronLeft
                        size="20"
                        color={query.page === 1 ? '#b3b1b1' : '#000'}
                      />
                    </button>
                    <div>
                      {pages.map(value => {
                        return (
                          <button
                            key={value}
                            onClick={() => handlePaginate(value)}
                            data-active={value === query.page}
                          >
                            {value}
                          </button>
                        )
                      })}
                    </div>
                    <button
                      disabled={
                        query.page === Math.ceil(data?.total / query.limit)
                      }
                      onClick={handlePageNext}
                    >
                      <Icon.ChevronRight
                        size="20"
                        color={
                          query.page === Math.ceil(data?.total / query.limit)
                            ? '#b3b1b1'
                            : '#000'
                        }
                      />
                    </button>
                  </CustomPagination>
                </div>
              </>
            )}
          </ListingBody>
        )}
        {!isActive && (
          <ActiveNPSWrapper>
            <div className="result">
              <img src={InfoIcon} />
              <div className="text__wrapper">
                <h3>
                  <FormattedMessage id="You have not yet activated the NPS module" />
                </h3>
              </div>
              <Show condition={userRole >= CLIENT_ADMIN}>
                <Link to="/account/settings?t=4">
                  <button className="button">
                    <FormattedMessage id="Activate now" />
                  </button>
                </Link>
              </Show>
            </div>
          </ActiveNPSWrapper>
        )}
      </Container>
    </LoadableComponent>
  )
}

const NPSWrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )

  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
        orderBy: StringParam,
        search: StringParam,
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 10),
        status: StringParam,
        review: withDefault(arrayParam, []),
        date: withDefault(arrayParam, []),
      }}
    >
      <NPS />
    </SearchParamsHandlerProvider>
  )
}

export default NPSWrapper
